import { useState, useEffect } from 'react';
import { Plug, Github, Slack, Webhook, Settings, CheckCircle, AlertCircle, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { settingsService } from '../../services/settingsService';
import type { Integration, Webhook as WebhookType, WebhookCreateRequest } from '../../services/settingsService';
import { toast } from 'react-hot-toast';

const IntegrationsTab = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [webhooks, setWebhooks] = useState<WebhookType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [newWebhook, setNewWebhook] = useState<WebhookCreateRequest>({ name: '', url: '', events: [] });
  const [showAddWebhook, setShowAddWebhook] = useState(false);
  const [addingWebhook, setAddingWebhook] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [integrationsData, webhooksData] = await Promise.all([
        settingsService.loadIntegrations(),
        settingsService.loadWebhooks()
      ]);
      setIntegrations(Array.isArray(integrationsData) ? integrationsData : []);
      setWebhooks(Array.isArray(webhooksData) ? webhooksData : []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load integrations';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const availableEvents = [
    'build.started', 'build.completed', 'build.failed',
    'deploy.success', 'deploy.failed',
    'error.critical', 'error.warning',
    'agent.started', 'agent.stopped'
  ];

  const getIntegrationIcon = (name: string) => {
    switch (name.toLowerCase()) {
      case 'github': return Github;
      case 'slack': return Slack;
      case 'discord': return Webhook;
      case 'jira': return Settings;
      default: return Plug;
    }
  };

  const handleConnectIntegration = async (integration: Integration) => {
    if (integration.connected) {
      try {
        setConnecting(integration.id);
        await settingsService.disconnectIntegration(integration.id);
        toast.success(`${integration.name} disconnected successfully`);
        await loadData();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : `Failed to disconnect ${integration.name}`;
        toast.error(errorMessage);
      } finally {
        setConnecting(null);
      }
    } else {
      try {
        setConnecting(integration.id);
        const { authUrl } = await settingsService.connectIntegration(integration.id);
        window.location.href = authUrl;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : `Failed to connect ${integration.name}`;
        toast.error(errorMessage);
        setConnecting(null);
      }
    }
  };

  const toggleWebhook = async (webhookId: string) => {
    try {
      const webhook = webhooks.find(w => w.id === webhookId);
      if (!webhook) return;

      const updatedWebhook = await settingsService.updateWebhook(webhookId, { 
        active: !webhook.active 
      });
      
      setWebhooks(prev => prev.map(w => 
        w.id === webhookId ? updatedWebhook : w
      ));
      
      toast.success(`Webhook ${updatedWebhook.active ? 'enabled' : 'disabled'}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update webhook';
      toast.error(errorMessage);
    }
  };

  const addWebhook = async () => {
    if (!newWebhook.name || !newWebhook.url || newWebhook.events.length === 0) {
      toast.error('Please fill in all fields and select at least one event');
      return;
    }

    setAddingWebhook(true);
    try {
      const createdWebhook = await settingsService.createWebhook(newWebhook);
      setWebhooks(prev => [...prev, createdWebhook]);
      setNewWebhook({ name: '', url: '', events: [] });
      setShowAddWebhook(false);
      toast.success('Webhook created successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create webhook';
      toast.error(errorMessage);
    } finally {
      setAddingWebhook(false);
    }
  };

  const deleteWebhook = async (webhookId: string) => {
    try {
      await settingsService.deleteWebhook(webhookId);
      setWebhooks(prev => prev.filter(w => w.id !== webhookId));
      toast.success('Webhook deleted successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete webhook';
      toast.error(errorMessage);
    }
  };

  const testWebhook = async (webhookId: string) => {
    try {
      const result = await settingsService.testWebhook(webhookId);
      toast.success(result.message);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to test webhook';
      toast.error(errorMessage);
    }
  };

  const testIntegration = async (integrationId: string) => {
    try {
      setTesting(integrationId);
      const result = await settingsService.testIntegration(integrationId);
      
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
      
      await loadData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to test integration';
      toast.error(errorMessage);
    } finally {
      setTesting(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Integrations</h2>
          <p className="text-slate-400 mt-1">Connect Beehive with your favorite tools and services</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 size={32} className="animate-spin text-brand-accent" />
          <span className="ml-3 text-slate-400">Loading integrations...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Integrations</h2>
          <p className="text-slate-400 mt-1">Connect Beehive with your favorite tools and services</p>
        </div>
        <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400">{error}</p>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={loadData}
            className="mt-3"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Integrations</h2>
        <p className="text-slate-400 mt-1">Connect Beehive with your favorite tools and services</p>
      </div>

      <Card data-devinid="integrations-connected-services-section" className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Plug size={20} className="text-brand-accent" />
            <span>Connected Services</span>
          </CardTitle>
          <CardDescription>Manage your third-party integrations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {integrations.map((integration) => {
              const IconComponent = getIntegrationIcon(integration.name);
              const isConnecting = connecting === integration.id;
              const isTesting = testing === integration.id;
              
              return (
                <div key={integration.id} className="p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg border border-slate-700/40">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-brand-primary/20 rounded-lg">
                        <IconComponent size={20} className="text-brand-accent" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium cream-gradient-text text-drop-shadow">{integration.name}</h4>
                        <p className="text-xs text-slate-400">{integration.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {integration.connected ? (
                        <CheckCircle size={16} className="text-green-400" />
                      ) : (
                        <AlertCircle size={16} className="text-slate-400" />
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-slate-400">
                      Last sync: {integration.lastSync}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        data-devinid={`integration-${integration.id}-test-button`}
                        size="sm"
                        variant="ghost"
                        onClick={() => testIntegration(integration.id)}
                        disabled={isTesting || isConnecting}
                      >
                        {isTesting ? (
                          <>
                            <Loader2 size={14} className="mr-2 animate-spin" />
                            Testing...
                          </>
                        ) : (
                          'Test Connection'
                        )}
                      </Button>
                      <Button
                        data-devinid={`integration-${integration.id}-connect-button`}
                        size="sm"
                        variant={integration.connected ? "ghost" : "default"}
                        onClick={() => handleConnectIntegration(integration)}
                        disabled={isConnecting || isTesting}
                      >
                        {isConnecting ? (
                          <>
                            <Loader2 size={14} className="mr-2 animate-spin" />
                            {integration.connected ? 'Disconnecting...' : 'Connecting...'}
                          </>
                        ) : (
                          integration.connected ? 'Disconnect' : 'Connect'
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card data-devinid="integrations-webhooks-section" className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Webhook size={20} className="text-brand-accent" />
              <span>Webhooks</span>
            </div>
            <Button data-devinid="add-webhook-button" onClick={() => setShowAddWebhook(!showAddWebhook)} size="sm">
              <Plug size={16} className="mr-2" />
              Add Webhook
            </Button>
          </CardTitle>
          <CardDescription>Configure webhooks to receive real-time notifications</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {showAddWebhook && (
            <div className="p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg border border-brand-primary/20">
              <h4 className="text-sm font-medium cream-gradient-text text-drop-shadow mb-4">Add New Webhook</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Name</label>
                  <Input
                    data-devinid="webhook-name-input"
                    value={newWebhook.name}
                    onChange={(e) => setNewWebhook(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Webhook name"
                    className="violett-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">URL</label>
                  <Input
                    data-devinid="webhook-url-input"
                    value={newWebhook.url}
                    onChange={(e) => setNewWebhook(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://your-webhook-url.com"
                    className="violett-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Events</label>
                  <div className="grid grid-cols-2 gap-2">
                    {availableEvents.map(event => (
                      <label key={event} className="flex items-center space-x-2 text-sm">
                        <input
                          data-devinid={`webhook-event-${event}-checkbox`}
                          type="checkbox"
                          checked={newWebhook.events.includes(event)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewWebhook(prev => ({ ...prev, events: [...prev.events, event] }));
                            } else {
                              setNewWebhook(prev => ({ ...prev, events: prev.events.filter(e => e !== event) }));
                            }
                          }}
                          className="rounded border-slate-600 bg-slate-700 text-brand-primary focus:ring-brand-primary"
                        />
                        <span className="text-slate-300">{event}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex space-x-3">
                  <Button 
                    data-devinid="webhook-add-submit-button"
                    onClick={addWebhook}
                    disabled={addingWebhook}
                  >
                    {addingWebhook ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      'Add Webhook'
                    )}
                  </Button>
                  <Button data-devinid="webhook-add-cancel-button" variant="ghost" onClick={() => setShowAddWebhook(false)}>Cancel</Button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {webhooks.map((webhook) => (
              <div key={webhook.id} className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
                <div className="flex items-center space-x-3 flex-1">
                  <div className="p-2 bg-brand-primary/20 rounded-lg">
                    <Webhook size={16} className="text-brand-accent" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm font-medium text-brand-text">{webhook.name}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        webhook.active ? 'bg-green-400/20 text-green-400' : 'bg-slate-400/20 text-slate-400'
                      }`}>
                        {webhook.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 mb-1">{webhook.url}</div>
                    <div className="flex flex-wrap gap-1">
                      {webhook.events.map(event => (
                        <span key={event} className="px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded">
                          {event}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    data-devinid={`webhook-${webhook.id}-test-button`}
                    variant="ghost"
                    size="sm"
                    onClick={() => testWebhook(webhook.id)}
                  >
                    Test
                  </Button>
                  <Button
                    data-devinid={`webhook-${webhook.id}-toggle-button`}
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleWebhook(webhook.id)}
                  >
                    {webhook.active ? 'Disable' : 'Enable'}
                  </Button>
                  <Button 
                    data-devinid={`webhook-${webhook.id}-delete-button`}
                    variant="ghost" 
                    size="sm" 
                    className="text-red-400 hover:text-red-300"
                    onClick={() => deleteWebhook(webhook.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings size={20} className="text-brand-accent" />
            <span>API Access</span>
          </CardTitle>
          <CardDescription>Manage API keys and access tokens</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <h4 className="text-sm font-medium text-blue-400 mb-2">🔗 Integration Guidelines</h4>
              <ul className="text-sm text-slate-400 space-y-1">
                <li>• Use webhooks for real-time notifications</li>
                <li>• Test integrations in development first</li>
                <li>• Monitor webhook delivery status</li>
                <li>• Keep API keys secure and rotate regularly</li>
              </ul>
            </div>
            
            <div className="flex space-x-3">
              <Button variant="ghost" onClick={() => window.open('https://docs.beehive.ai/integrations', '_blank')}>
                View API Documentation
              </Button>
              <Button variant="ghost" onClick={loadData}>
                Refresh Integrations
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IntegrationsTab;
